import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { debounce } from "lodash";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { SCREEN_SIZE } from "src/stores/UIStore";
import styles from "./Creditors.module.css";
import { InvoiceList } from "./list/InvoiceList";
import { INVOICE_LIST_STATE, INVOICE_TYPE, SORT } from "src/stores/krediflow/InvoiceEnums";
import { TagSearch } from "src/components/tagSearch/TagSearch";
import { Route, selectRoute } from "src/config/routes";
import { ImportInvoicesInput } from "./importInvoices/input/ImportInvoicesInput";

export const getInvoiceTypeIcon = (type?: INVOICE_TYPE) => {
    switch (type) {
        case INVOICE_TYPE.INVOICE:
            return "/icons/cred/invoice-type.svg";
        case INVOICE_TYPE.CREDIT:
            return "/icons/cred/credit-type.svg";
        default:
            return "/icons/cred/credit-type.svg";
    }
};

const CreditorsBase = () => {
    const { t } = useTranslation();
    const { uiStore, navStore, invoiceListStore, authStore, importInvoiceFilesStore } = useContext(RootStoreContext);
    const { setFilter, invoiceFilter } = invoiceListStore;

    useEffect(() => {
        navStore.setTitle("");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(null);
    }, [navStore]);

    useEffect(() => {
        // Set user ID if "nur meine" is active by default
        const showOnlyMine = invoiceListStore.searchTags.some(
            (tag) => tag.id === "SHOW_ONLY_MY_OWN_INVOICES" && tag.active
        );
        if (showOnlyMine && authStore.user?.userid) {
            // Format as PostgreSQL array literal
            invoiceListStore.setAssignedUserIds(`{${authStore.user.userid}}`);
        }

        invoiceListStore.initialLoadInvoices();

        // Load all invoices once component is rendered
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        invoiceListStore.startLoadInvoicesTimer();
        return () => {
            invoiceListStore.clearLoadInvoicesTimer();
        };
    }, [invoiceListStore]);

    const onFilterChange = useCallback(
        (invoiceListState: INVOICE_LIST_STATE) => {
            setFilter({ ...invoiceFilter, state: invoiceListState });

            // Map UI State to Server State Filter
            let serverStateFilter: string;
            switch (invoiceListState) {
                case INVOICE_LIST_STATE.NEW:
                    serverStateFilter = "{10,15,20,25}";
                    break;
                case INVOICE_LIST_STATE.TO_APPROVE:
                    serverStateFilter = "{30,41,42,43}";
                    break;
                case INVOICE_LIST_STATE.PAY:
                    serverStateFilter = "{49,50,60}";
                    break;
                case INVOICE_LIST_STATE.PAID:
                    serverStateFilter = "{90}";
                    break;
                case INVOICE_LIST_STATE.REJECTED:
                    serverStateFilter = "{95}";
                    break;
                case INVOICE_LIST_STATE.CANCELLED:
                    serverStateFilter = "{99}";
                    break;
                case INVOICE_LIST_STATE.ALL_OPEN:
                    serverStateFilter = "{10,15,20,25,30,41,42,43,49,50,60,95}";
                    break;
                case INVOICE_LIST_STATE.ALL:
                    serverStateFilter = "{10,15,20,25,30,41,42,43,49,50,60,90,95,99}";
                    break;
                default:
                    serverStateFilter = "{10,15,20,25}";
            }

            invoiceListStore.setPageOffset(0, false);
            invoiceListStore.setStateFilter(serverStateFilter);
            invoiceListStore.loadInvoices();

            invoiceListStore.deselectAllInvoices();
            invoiceListStore.resetAreAllInvoicesOfStateSelected();
        },
        [invoiceFilter, setFilter, invoiceListStore]
    );

    const onUpdateSearchTag = useCallback(
        (searchTagId: string, status: boolean) => {
            invoiceListStore.setTag(searchTagId, status);

            // Update server-side type filters based on active tags
            const updatedTags = invoiceListStore.searchTags.map((tag) =>
                tag.id === searchTagId ? { ...tag, active: status } : tag
            );

            const activeTypeFilters = updatedTags
                .filter((tag) => tag.active && tag.type !== undefined)
                .map((tag) => tag.type);

            if (activeTypeFilters.length > 0) {
                invoiceListStore.setTypeFilters(`{${activeTypeFilters.join(",")}}`);
            } else {
                invoiceListStore.setTypeFilters(null);
            }

            // Handle "nur meine" (SHOW_ONLY_MY_OWN_INVOICES) filter
            const showOnlyMine = updatedTags.find((tag) => tag.id === "SHOW_ONLY_MY_OWN_INVOICES")?.active || false;
            if (showOnlyMine && authStore.user?.userid) {
                // Format as PostgreSQL array literal
                invoiceListStore.setAssignedUserIds(`{${authStore.user.userid}}`);
            } else {
                invoiceListStore.setAssignedUserIds(null);
            }

            // Handle "meine Liegenschaften" (SHOW_ONLY_INVOICES_FROM_MY_REALESTATES) filter
            const showMyRealestates =
                updatedTags.find((tag) => tag.id === "SHOW_ONLY_INVOICES_FROM_MY_REALESTATES")?.active || false;
            if (showMyRealestates && authStore.user?.userid) {
                // Use server-side filter that filters by user's realestate access
                invoiceListStore.setFilterRealestatesByUserIds(`{${authStore.user.userid}}`);
            } else {
                invoiceListStore.setFilterRealestatesByUserIds(null);
            }

            invoiceListStore.setPageOffset(0, false);
            invoiceListStore.loadInvoices();
        },
        [invoiceListStore, authStore.user?.userid]
    );

    // Debounced search function
    const debouncedSearch = useMemo(
        () =>
            debounce((query: string) => {
                invoiceListStore.setSearchQuery(query);
                invoiceListStore.setPageOffset(0, false);
                invoiceListStore.loadInvoices();
            }, 500),
        [invoiceListStore]
    );

    const onChangeSearchQuery = useCallback(
        (query: string) => {
            setFilter({ ...invoiceFilter, query });
            debouncedSearch(query);
        },
        [invoiceFilter, setFilter, debouncedSearch]
    );

    const onResetSearch = useCallback(() => {
        invoiceListStore.clearFilter();
        // Reset all server-side filters
        invoiceListStore.setSearchQuery("");
        invoiceListStore.setTypeFilters(null);
        invoiceListStore.setAssignedUserIds(null);
        invoiceListStore.setFilterRealestatesByUserIds(null);
        invoiceListStore.setPageOffset(0, false);
        invoiceListStore.loadInvoices();
    }, [invoiceListStore]);

    const onSortChange = useCallback(
        (newSort: SORT) => {
            setFilter({ ...invoiceFilter, sort: newSort });

            // Map to server-side sort direction
            const sortDirection = newSort === SORT.DATE_NEWEST ? "desc" : "asc";
            invoiceListStore.setSortDirection(sortDirection);
            invoiceListStore.loadInvoices();
        },
        [invoiceFilter, setFilter, invoiceListStore]
    );

    const sortItems = useMemo(
        () => [
            { label: t("screens.tickets.sort.dateNewest"), value: SORT.DATE_NEWEST },
            { label: t("screens.tickets.sort.dateOldest"), value: SORT.DATE_OLDEST }
        ],
        [t]
    );

    const addNewInvoiceLabel = (
        <div className="d-flex align-items-center">
            <UI.Icon icon={UI.SVGIcon.Plus} className="mr-2" />
            <div>{t("screens.kredi_flow.list.new_invoice_button")}</div>
        </div>
    );

    const addNewInvoiceButtonMobile = (
        <UI.Button
            to={selectRoute(Route.newInvoice, authStore.user?.role)}
            label={<UI.Icon icon={UI.SVGIcon.Plus} size={40} />}
            className={styles.AddButtonMobile}
            variant="outline-primary"
        />
    );

    const addNewInvoiceButtonDesktop = (
        <UI.Button
            to={selectRoute(Route.newInvoice, authStore.user?.role)}
            label={addNewInvoiceLabel}
            className={styles.AddButton}
            variant="outline-primary"
        />
    );

    const addNewInvoiceButton =
        uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? addNewInvoiceButtonMobile : addNewInvoiceButtonDesktop;

    const importDocumentsLabel = (
        <div className="d-flex align-items-center">
            <UI.Icon icon={UI.SVGIcon.UploadCloud} className="mr-2" />
            <div>{t("screens.kredi_flow.list.import_documents")}</div>
        </div>
    );

    const onClickImportDocumentsButton = useCallback(() => {
        if (importInvoiceFilesStore.fileInputRef) {
            importInvoiceFilesStore.fileInputRef.click();
        }
    }, [importInvoiceFilesStore]);

    const importDocumentsButtonMobile = (
        <div className={`${styles.ImportDocumentsInputButtonMobile} ml-2`} onClick={onClickImportDocumentsButton}>
            {<UI.Icon icon={UI.SVGIcon.UploadCloud} />}
            <ImportInvoicesInput handleClickManually={true} />
        </div>
    );

    const importDocumentsButtonDesktop = (
        <div className={`${styles.ImportDocumentsInputButton} ml-2`} onClick={onClickImportDocumentsButton}>
            {importDocumentsLabel}
            <ImportInvoicesInput handleClickManually={true} />
        </div>
    );

    const importDocumentsButton =
        uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? importDocumentsButtonMobile : importDocumentsButtonDesktop;

    const onClickBatchProcessingButton = useCallback(() => {
        invoiceListStore.setIsBatchProcessing(true);
    }, [invoiceListStore]);

    const batchProcessingButton = (
        <UI.Button
            to={selectRoute(Route.invoiceDetails, authStore.user?.role, {
                invoicenumber: String(invoiceListStore.selectedBatchProcessInvoiceNumbers[0])
            })}
            onClick={onClickBatchProcessingButton}
            className={`${styles.ButtonBatchProcessing} ${uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? "mt-2" : "ml-2"
                }`}
            label={t("screens.kredi_flow.batch_processing")}
            disabled={!invoiceListStore.hasAnySelectedBatchProcessInvoice}
        />
    );

    const onClickDownloadInvoiceFilesButton = useCallback(() => {
        invoiceListStore.downloadInvoiceFiles();
    }, [invoiceListStore]);

    const onClickDeleteInvoicesButton = useCallback(() => {
        invoiceListStore.deleteInvoices();
    }, [invoiceListStore]);

    const reloadAllInvoices = useCallback(() => {
        invoiceListStore.loadInvoices();
    }, [invoiceListStore]);

    const downloadInvoiceFilesButtonLabel = (
        <div className="d-flex align-items-center position-relative">
            <div>{t("labels.download")}</div>
            <UI.RotatingSpinner
                className={`${invoiceListStore.isDownloadingInvoiceFiles ? styles.ActionSpinner : "d-none"}`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    const downloadInvoiceFilesButton = (
        <UI.Button
            variant="outline-primary"
            onClick={onClickDownloadInvoiceFilesButton}
            className={styles.DownloadInvoiceFileButton}
            label={downloadInvoiceFilesButtonLabel}
            disabled={invoiceListStore.selectedFileIds.length === 0}
        />
    );

    const deleteInvoicesButtonLabel = (
        <div className="d-flex align-items-center position-relative">
            <div>{t("labels.delete")}</div>
            <UI.RotatingSpinner
                className={`${invoiceListStore.isDeletingInvoices ? styles.ActionSpinner : "d-none"}`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    const deleteInvoicesButton = (
        <UI.Button
            variant="outline-primary mr-2"
            onClick={onClickDeleteInvoicesButton}
            className={styles.DownloadInvoiceFileButton}
            label={deleteInvoicesButtonLabel}
            disabled={invoiceListStore.selectedFileIds.length === 0}
        />
    );

    const displayToolbarBatchProcessing =
        uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L && invoiceListStore.hasAnySelectedBatchProcessInvoice
            ? true
            : uiStore.currentScreenSize > SCREEN_SIZE.MOBILE_L
                ? true
                : false;

    return (
        <UI.Container
            className={`pt-5 ${styles.CreditorsContainer} ${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? styles.MinPadding : ""
                }`}
            fluid
        >
            <UI.Row className="creditors-header-line mb-2 align-items-center">
                <UI.Col xs={4} sm={8} lg={4}>
                    <UI.Title>{t("screens.kredi_flow.sidebar_title")}</UI.Title>
                </UI.Col>
                <UI.Col xs={8} lg={8}>
                    <div className="d-flex justify-content-end">
                        {addNewInvoiceButton}
                        {navStore.hasFeatureKrediFlowPro ? importDocumentsButton : undefined}
                    </div>
                </UI.Col>
            </UI.Row>
            <UI.Row>
                <UI.Col xs={12}>
                    <div className={`${styles.Toolbar} mb-3`}>
                        <div className={styles.ToolbarTagSearch}>
                            <TagSearch
                                searchTags={invoiceListStore.searchTags}
                                value={invoiceFilter.query}
                                updateTagStatusById={onUpdateSearchTag}
                                onChangeInput={onChangeSearchQuery}
                                reset={onResetSearch}
                            />
                        </div>
                        <div className={styles.ToolbarSort}>
                            <UI.Dropdown
                                id="sort"
                                onSelect={onSortChange}
                                items={sortItems}
                                label={uiStore.isMobile ? "" : t(`screens.tickets.sort.${invoiceFilter.sort}`)}
                                icon={uiStore.isMobile && <UI.Icon icon={UI.SVGIcon.Sort} />}
                            />
                        </div>
                        <div className={styles.ToolbarLoadingInfo}>
                            <UI.Icon
                                onClick={reloadAllInvoices}
                                style={{ cursor: "pointer" }}
                                className="mr-2"
                                icon={UI.SVGIcon.Refresh}
                                color="var(--primary)"
                            />
                            <UI.RotatingSpinner
                                className={`${invoiceListStore.isLoadingInvoices ? "" : "d-none"}`}
                                noLogo={true}
                                size={20}
                            />
                            <div
                                className={`${styles.ToolbarLoadingInfoText} ${invoiceListStore.isLoadingInvoices ? "d-none" : ""
                                    }`}
                            >
                                {invoiceListStore.lastReloadTimeText}
                            </div>
                        </div>
                        {navStore.hasFeatureKrediFlowPro && displayToolbarBatchProcessing && (
                            <div
                                className={`${styles.ToolbarBatchProcessing} ${invoiceListStore.invoiceFilter.state === INVOICE_LIST_STATE.CANCELLED
                                    ? styles.MoreSpace
                                    : ""
                                    }`}
                            >
                                {invoiceListStore.invoiceFilter.state === INVOICE_LIST_STATE.CANCELLED &&
                                    deleteInvoicesButton}
                                {downloadInvoiceFilesButton}
                                {batchProcessingButton}
                            </div>
                        )}
                    </div>
                </UI.Col>
            </UI.Row>
            <UI.Row>
                <UI.Col xs={12}>
                    <InvoiceList onFilterChange={onFilterChange} />
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const Creditors = Sentry.withProfiler(observer(CreditorsBase));
